import { Auth } from 'aws-amplify';
import {
  RequestResetPasswordForm,
  RequestResetPasswordFormData,
} from 'domains/auth/PasswordReset/RequestResetPasswordForm';
import { ResetPasswordForm, ResetPasswordFormData } from 'domains/auth/PasswordReset/ResetPasswordForm';
import { ReactNode, useMemo, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { useBoolean } from 'react-use';
import { RequestResetPasswordFormContext } from './context';

type Props = {
  onSuccessfulReset?: (data: { email: string; password: string }) => void;

  /** To be rendered above form components, used for headings, etc that wrap the form */
  children?: (props: { email: string | null }) => ReactNode;

  className?: string;

  /** On return to login button */
  onBack?: () => void;
};

export const PasswordReset = ({ onSuccessfulReset: onSuccessfulResetProp, children, className, onBack }: Props) => {
  const [email, setEmail] = useState<string | null>(null);
  const [loading, setLoading] = useBoolean(false);

  const navigate = useNavigate();

  const onSuccessfulReset = useMemo(() => onSuccessfulResetProp || (() => navigate('/')), [onSuccessfulResetProp]);

  const onSubmitEmail = (data: RequestResetPasswordFormData) => {
    setEmail(data.username);
    return Auth.forgotPassword(data.username);
  };

  const onSubmitReset = async ({ code, password }: ResetPasswordFormData) => {
    setLoading(true);

    if (email) {
      return Auth.forgotPasswordSubmit(email, code, password)
        .then(() => {
          return onSuccessfulReset({ email, password });
        })
        .finally(() => setLoading(false));
    }
    return false;
  };

  return (
    <>
      <RequestResetPasswordFormContext.Provider value={{ onBack, formClassname: className }}>
        {children?.({ email })}
        {email === null && <RequestResetPasswordForm onSubmit={onSubmitEmail} loading={loading} />}
        {email && <ResetPasswordForm onSubmit={onSubmitReset} loading={loading} />}
      </RequestResetPasswordFormContext.Provider>
    </>
  );
};
